import {
  type ChangeEvent,
  createContext,
  type ElementType,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Button from "./Button";
import CardAction from "./CardAction";

interface SelectableItem {
  id: number | string;
}

interface SelectableContextValue {
  items: SelectableItem[];
  checked: Set<number | string>;
  checkHandler: (
    id: number | string,
    pos: number,
    e: ChangeEvent<HTMLInputElement>,
  ) => void;
  checkAllHandler: (e: ChangeEvent<HTMLInputElement>) => void;
  resetChecked: () => void;
}

const SelectableContext = createContext<SelectableContextValue>({
  items: [],
  checked: new Set(),
  checkHandler: () => {},
  checkAllHandler: () => {},
  resetChecked: () => {},
});

interface SelectableProps {
  items: SelectableItem[];
  children: ReactNode;
  rangeSelect?: boolean;
  onChange?: (checked: Set<number | string>) => void;
  defaultSelectAll?: boolean;
  defaultSelectedIds?: (number | string)[];
}

export function Selectable({
  items,
  children,
  rangeSelect,
  onChange,
  defaultSelectAll,
  defaultSelectedIds,
}: SelectableProps) {
  const [checked, setChecked] = useState<Set<number | string>>(() => {
    if (defaultSelectedIds && defaultSelectedIds.length > 0) {
      return new Set(defaultSelectedIds);
    }

    const initialChecked = new Set<number | string>();
    if (defaultSelectAll && items.length > 0) {
      items.forEach((item) => initialChecked.add(item.id));
    }
    return initialChecked;
  });

  const [lastClickedItem, setLastClickedItem] = useState<{
    pos: number;
    checked: boolean;
  } | null>(null);

  useEffect(() => {
    if (onChange) {
      onChange(checked);
    }
  }, [checked, onChange]);

  function checkAllHandler(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.checked) {
      setChecked(new Set(items.map((item) => item.id)));
    } else {
      setChecked(new Set());
    }
  }

  function resetChecked() {
    setChecked(new Set());
    setLastClickedItem(null);
  }

  function checkHandler(
    id: number | string,
    pos: number,
    e: ChangeEvent<HTMLInputElement>,
  ) {
    setChecked((curChecked) => {
      const newChecked = new Set(curChecked);
      if (
        rangeSelect &&
        e.nativeEvent.type === "click" &&
        (e.nativeEvent as MouseEvent).shiftKey &&
        lastClickedItem
      ) {
        for (
          let i = Math.min(lastClickedItem.pos, pos);
          i <= Math.max(lastClickedItem.pos, pos);
          i++
        ) {
          if (lastClickedItem.checked) {
            newChecked.delete(items[i].id);
          } else {
            newChecked.add(items[i].id);
          }
        }
      } else {
        if (curChecked.has(id)) {
          newChecked.delete(id);
        } else {
          newChecked.add(id);
        }
      }
      setLastClickedItem({ pos, checked: curChecked.has(id) });

      return newChecked;
    });
  }

  return (
    <SelectableContext.Provider
      value={{ items, checked, checkHandler, checkAllHandler, resetChecked }}
    >
      {children}
    </SelectableContext.Provider>
  );
}

interface CheckboxProps {
  value?: number | string;
  index?: number;
  selectAll?: boolean;
  disabled?: boolean;
  className?: string;
}

function Checkbox({
  value,
  index,
  selectAll,
  disabled = false,
  className,
}: CheckboxProps) {
  const { items, checked, checkHandler, checkAllHandler } =
    useContext(SelectableContext);
  const checkboxref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectAll && checkboxref.current) {
      checkboxref.current.indeterminate =
        checked.size > 0 && checked.size < items.length;
    }
  }, [checked.size, items.length, selectAll]);

  return (
    <input
      ref={checkboxref}
      className={className}
      type="checkbox"
      disabled={disabled}
      checked={
        selectAll
          ? checked.size === items.length && checked.size > 0
          : checked.has(value!)
      }
      onChange={(e) => {
        if (selectAll) {
          checkAllHandler(e);
        } else {
          checkHandler(value!, index!, e);
        }
      }}
    />
  );
}

interface ActionButtonProps {
  children: ReactNode;
  resetOnClick?: boolean;
  onClick: (checkedIds: (number | string)[], resetChecked: () => void) => void;
  [key: string]: unknown;
}

function ActionButton({
  children,
  resetOnClick,
  onClick,
  ...rest
}: ActionButtonProps) {
  const { checked, resetChecked } = useContext(SelectableContext);
  return (
    <Button
      type="button"
      disabled={checked.size === 0}
      onClick={() => {
        onClick(Array.from(checked), resetChecked);
        if (resetOnClick) {
          resetChecked();
        }
      }}
      {...rest}
    >
      {children}
    </Button>
  );
}

interface CardActionButtonProps {
  resetOnClick?: boolean;
  onClick: (checkedIds: (number | string)[], resetChecked: () => void) => void;
  title: string;
  description?: string;
  icon?: ElementType;
  [key: string]: unknown;
}

function CardActionButton({
  resetOnClick,
  onClick,
  title,
  description,
  icon,
}: CardActionButtonProps) {
  const { checked, resetChecked } = useContext(SelectableContext);
  return (
    <CardAction
      icon={icon}
      description={description}
      title={title}
      onClick={() => {
        onClick(Array.from(checked), resetChecked);
        if (resetOnClick) {
          resetChecked();
        }
      }}
    />
  );
}

interface ActionProps {
  render: (
    checkedIds: (number | string)[],
    resetChecked: () => void,
  ) => ReactNode;
}

function Action({ render }: ActionProps) {
  const { checked, resetChecked } = useContext(SelectableContext);
  return render(Array.from(checked), resetChecked);
}

Selectable.Checkbox = Checkbox;
Selectable.ActionButton = ActionButton;
Selectable.CardActionButton = CardActionButton;
Selectable.Action = Action;
